import { useRef, useState, useEffect } from 'react'
import { motion, useScroll, useTransform, useInView } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  ArrowRight, 
  ArrowUpRight, 
  Heart, 
  Briefcase, 
  PartyPopper, 
  Users, 
  Calendar, 
  MapPin, 
  Star,
  Sparkles,
  Award,
  Phone,
  CheckCircle2,
  Quote
} from 'lucide-react'

// Event-Kategorien Daten
const eventCategories = [
  {
    id: 'hochzeiten',
    title: 'Hochzeiten',
    subtitle: 'Your Forever Starts Here',
    tagline: 'Ihr schönster Tag',
    description: 'Traumhafte Hochzeitsfeiern in historischem Ambiente. Von der freien Trauung im Innenhof bis zur rauschenden Feier in der Festscheune.',
    image: '/home_pics/hochzeit.jpeg',
    link: '/hochzeiten',
    capacity: 'Bis 400 Gäste',
    icon: Heart,
    features: ['Freie Trauungen', 'Exklusive Nutzung', 'All-Inclusive Pakete']
  },
  {
    id: 'corporate',
    title: 'Firmenfeiern',
    subtitle: 'Where Business Meets Elegance',
    tagline: 'Professionell & beeindruckend',
    description: 'Weihnachtsfeiern, Sommerfeste, Tagungen und Kongresse. Moderne Technik trifft auf historischen Charme.',
    image: '/assets/home_pics/HK_184.jpg',
    link: '/unternehmensfeiern',
    capacity: 'Bis 2.000 Gäste',
    icon: Briefcase,
    features: ['Großveranstaltungen', 'Event-Technik', 'Full-Service Catering']
  },
  {
    id: 'private',
    title: 'Private Feiern',
    subtitle: 'Celebrate Life\'s Moments',
    tagline: 'Unvergessliche Momente',
    description: 'Geburtstage, Jubiläen und besondere Anlässe. Intime Feiern in außergewöhnlichem Ambiente.',
    image: '/assets/home_pics/DS2.JPG',
    link: '/private-feiern',
    capacity: 'Flexibel ab 30 Gäste',
    icon: PartyPopper,
    features: ['Individuelle Planung', 'Persönliche Betreuung', 'Maßgeschneiderte Menüs']
  }
]

// Statistiken
const stats = [
  { number: '25+', label: 'Jahre Erfahrung', icon: Award },
  { number: '2.000', label: 'Max. Gäste', icon: Users },
  { number: '500+', label: 'Events pro Jahr', icon: Calendar },
  { number: '5', label: 'Einzigartige Räume', icon: MapPin }
]

// Warum Hückels May
const whyUs = [
  {
    icon: Sparkles,
    title: 'Historisch einzigartig',
    description: 'Authentische Räume'
  },
  {
    icon: Users,
    title: 'Flexibel für jede Größe',
    description: '30 bis 2.000 Gäste'
  },
  {
    icon: Star,
    title: 'Rundum-Service',
    description: 'Alles aus einer Hand'
  },
  {
    icon: Heart,
    title: 'Mit Leidenschaft',
    description: '25+ Jahre Erfahrung'
  }
]

export default function Veranstaltung() {
  const heroRef = useRef<HTMLDivElement>(null)
  const [activeCategory, setActiveCategory] = useState(0)
  
  const { scrollYProgress } = useScroll()
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0])
  const heroScale = useTransform(scrollYProgress, [0, 0.15], [1, 1.1])

  // Auto-rotate categories
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveCategory((prev) => (prev + 1) % eventCategories.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  return (
    <div className="bg-[#0a0a0a]">
      
      {/* ════════════════════════════════════════════════════════════
          HERO SECTION - Cinematic with Image
      ════════════════════════════════════════════════════════════ */}
      <section ref={heroRef} className="relative h-[50vh] min-h-[400px] max-h-[550px] w-full overflow-hidden">
        {/* Image Background with Parallax */}
        <motion.div style={{ scale: heroScale }} className="absolute inset-0">
          <img
            src="/assets/heros/hero_bilder.jpg"
            alt="Veranstaltung bei Hückels May"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.35)' }}
          />
        </motion.div>

        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-[#0a0a0a]" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent" />
        
        {/* Subtle Ruby Glow */}
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-brand-800/10 rounded-full blur-[150px]" />
        
        {/* Hero Content */}
        <motion.div 
          style={{ opacity: heroOpacity }}
          className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-8"
          >
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full">
              <Sparkles className="w-4 h-4 text-brand-400" />
              <span className="text-white/70 text-sm uppercase tracking-[0.3em]">Seit 1998</span>
            </div>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.4 }}
            className="font-serif text-4xl md:text-6xl lg:text-8xl text-white font-light tracking-tight leading-[0.95] mb-8"
          >
            Ihre Veranstaltung,<br />
            <span className="italic text-white/70">unvergesslich</span> gestaltet
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.7 }}
            className="text-white/50 text-lg md:text-xl max-w-2xl mb-12 leading-relaxed"
          >
            Von der Traumhochzeit bis zum exklusiven Firmenevent – wir bieten den perfekten Rahmen für Ihre besonderen Anlässe.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Link to="/kontakt">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-10 py-4 bg-white text-black text-xs uppercase tracking-[0.3em] font-bold rounded-full hover:bg-brand-800 hover:text-white transition-all duration-500"
              >
                Jetzt anfragen
              </motion.button>
            </Link>
            <Link to="/raeume">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-10 py-4 border border-white/30 text-white text-xs uppercase tracking-[0.3em] font-bold rounded-full hover:bg-white/10 transition-all duration-500 flex items-center gap-3"
              >
                Räume entdecken
                <ArrowRight className="w-4 h-4" />
              </motion.button>
            </Link>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border border-white/30 rounded-full flex justify-center pt-2"
          >
            <motion.div className="w-1 h-2 bg-white/60 rounded-full" />
          </motion.div>
        </motion.div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          INTRO SECTION - Elegant Statement
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-32 md:py-48 px-6">
        {/* Background Glows */}
        <div className="absolute top-20 left-20 w-[500px] h-[500px] bg-brand-800/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-20 right-20 w-[400px] h-[400px] bg-brand-900/5 rounded-full blur-[100px]" />
        
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-8"
          >
            Über 25 Jahre Erfahrung
          </motion.p>
          
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2 }}
            className="font-serif text-3xl md:text-5xl lg:text-6xl text-white font-light leading-tight mb-12"
          >
            Wir verwandeln Ihre Ideen in 
            <span className="text-white/40"> unvergessliche Erlebnisse.</span>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Link to="/geschichte" className="inline-flex items-center gap-3 text-white/50 hover:text-white transition-colors group">
              <span className="text-sm uppercase tracking-[0.2em]">Unsere Geschichte</span>
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          STATS BAR
      ════════════════════════════════════════════════════════════ */}
      <section className="border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="py-10 md:py-12 px-6 text-center border-r border-white/5 last:border-r-0 group hover:bg-white/[0.02] transition-colors flex flex-col items-center justify-center h-[140px]"
              >
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-brand-800/10 border border-brand-800/20 mb-3 group-hover:scale-110 transition-transform">
                  <stat.icon className="w-4 h-4 text-brand-400" />
                </div>
                <p className="text-white text-sm font-medium mb-1">{stat.number}</p>
                <p className="text-white/40 text-xs">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          EVENT CATEGORIES - Interactive Showcase
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-32 md:py-48">
        {/* Background Glows */}
        <div className="absolute top-1/4 right-10 w-[500px] h-[500px] bg-brand-800/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 left-10 w-[400px] h-[400px] bg-brand-900/5 rounded-full blur-[100px]" />
        
        <div className="max-w-[1600px] mx-auto px-6 relative z-10">
          
          {/* Section Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 md:mb-24">
            <div>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4"
              >
                Ihr Event, Ihre Wahl
              </motion.p>
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="font-serif text-4xl md:text-6xl lg:text-7xl text-white font-light"
              >
                Was möchten Sie<br />
                <span className="italic text-white/60">feiern?</span>
              </motion.h2>
            </div>
            
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="mt-8 md:mt-0"
            >
              <Link to="/veranstaltungen" className="inline-flex items-center gap-3 text-white/50 hover:text-white transition-colors group">
                <span className="text-sm uppercase tracking-[0.2em]">Alle Optionen</span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-2" />
              </Link>
            </motion.div>
          </div>

          {/* Interactive Category Display */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            
            {/* Left: Image */}
            <motion.div 
              className="relative aspect-[4/5] rounded-3xl overflow-hidden"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              {eventCategories.map((cat, index) => (
                <motion.img
                  key={cat.id}
                  src={cat.image}
                  alt={cat.title}
                  className="absolute inset-0 w-full h-full object-cover"
                  initial={{ opacity: 0 }}
                  animate={{ 
                    opacity: activeCategory === index ? 1 : 0,
                    scale: activeCategory === index ? 1 : 1.1
                  }}
                  transition={{ duration: 0.8 }}
                />
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
              
              {/* Capacity Badge */}
              <div className="absolute bottom-8 left-8 px-6 py-3 bg-white/10 backdrop-blur-xl rounded-full border border-white/10">
                <div className="flex items-center gap-3">
                  <Users className="w-4 h-4 text-white/60" />
                  <span className="text-white text-sm">{eventCategories[activeCategory].capacity}</span>
                </div>
              </div>
              
              {/* Icon Badge */}
              <div className="absolute top-8 left-8 w-14 h-14 bg-brand-800/80 backdrop-blur-xl rounded-2xl border border-brand-700/50 flex items-center justify-center">
                {(() => {
                  const IconComponent = eventCategories[activeCategory].icon
                  return <IconComponent className="w-6 h-6 text-white" />
                })()}
              </div>
            </motion.div>

            {/* Right: Category List */}
            <div className="space-y-6">
              {eventCategories.map((category, index) => (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Link 
                    to={category.link}
                    onMouseEnter={() => setActiveCategory(index)}
                    className="group block"
                  >
                    <div className={`p-8 rounded-2xl border transition-all duration-500 ${
                      activeCategory === index 
                        ? 'bg-white/5 border-white/20' 
                        : 'bg-transparent border-white/5 hover:border-white/10'
                    }`}>
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <p className="text-brand-400 text-xs uppercase tracking-[0.3em] mb-2">{category.tagline}</p>
                          <h3 className="font-serif text-3xl md:text-4xl text-white font-light">{category.title}</h3>
                        </div>
                        <div className={`w-12 h-12 rounded-full border flex items-center justify-center transition-all duration-500 ${
                          activeCategory === index
                            ? 'bg-brand-800 border-brand-800'
                            : 'border-white/10 group-hover:border-white/30'
                        }`}>
                          <ArrowUpRight className={`w-5 h-5 transition-all duration-500 ${
                            activeCategory === index ? 'text-white' : 'text-white/40 group-hover:text-white'
                          }`} />
                        </div>
                      </div>
                      <p className="text-white/40 leading-relaxed mb-4">{category.description}</p>
                      
                      {/* Features */}
                      <div className="flex flex-wrap gap-2">
                        {category.features.map((feature, idx) => (
                          <span 
                            key={idx}
                            className={`text-xs px-3 py-1 rounded-full transition-all duration-500 ${
                              activeCategory === index
                                ? 'bg-brand-800/20 text-brand-400 border border-brand-800/30'
                                : 'bg-white/5 text-white/50 border border-white/10'
                            }`}
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          WHY US
      ════════════════════════════════════════════════════════════ */}
      <section className="border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4">
            {whyUs.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="py-10 md:py-12 px-6 text-center border-r border-white/5 last:border-r-0 group hover:bg-white/[0.02] transition-colors flex flex-col items-center justify-center h-[140px] overflow-hidden"
              >
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-brand-800/10 border border-brand-800/20 mb-3 group-hover:scale-110 transition-transform">
                  <item.icon className="w-4 h-4 text-brand-400" />
                </div>
                <p className="text-white text-sm font-medium mb-1">{item.title}</p>
                <p className="text-white/40 text-xs">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          TESTIMONIAL / QUOTE
      ════════════════════════════════════════════════════════════ */}
      <section className="py-32 md:py-48 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Quote className="w-12 h-12 text-brand-800/30 mx-auto mb-8" />
            
            <blockquote className="font-serif text-2xl md:text-4xl text-white font-light leading-relaxed mb-8 italic">
              "Die perfekte Kombination aus historischem Ambiente und professionellem Service. Jedes Detail wurde mit größter Sorgfalt umgesetzt."
            </blockquote>
            
            <div className="flex justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 text-brand-400 fill-brand-400" />
              ))}
            </div>
            
            <p className="text-white/40 text-sm uppercase tracking-[0.2em]">
              Müller GmbH — Firmenevent 2024
            </p>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          CTA SECTION - Final Push
      ════════════════════════════════════════════════════════════ */}
      <section className="py-12 md:py-20">
        {/* Background Glows */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-800/5 rounded-full blur-[150px]" />
        
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-brand-800/10 border border-brand-800/20 mb-8">
              <Phone className="w-9 h-9 text-brand-400" />
            </div>
            
            <p className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-6">Bereit?</p>
            
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl text-white font-light mb-8 leading-tight">
              Lassen Sie uns Ihre<br />
              <span className="italic text-white/60">Veranstaltung</span> planen
            </h2>
            
            <p className="text-white/40 text-lg md:text-xl mb-12 max-w-2xl mx-auto">
              Kostenlose Beratung und ein unverbindliches Angebot – wir freuen uns auf Ihre Anfrage.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/kontakt">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-14 py-5 bg-white text-black text-sm uppercase tracking-[0.3em] font-bold rounded-full hover:bg-brand-800 hover:text-white transition-all duration-500 flex items-center gap-3"
                >
                  <Phone className="w-5 h-5" />
                  Kontakt aufnehmen
                </motion.button>
              </Link>
              <Link to="/raeume">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-14 py-5 border border-white/20 text-white text-sm uppercase tracking-[0.3em] font-bold rounded-full hover:bg-white/5 transition-all duration-500 flex items-center gap-3"
                >
                  Räume ansehen
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  )
}
